import React, { useState, useEffect } from 'react';
import { 
  Bell, Send, Clock, Calendar, Check, X, Info, 
  MessageSquare, Mail, Smartphone, AlertCircle,
  ChevronDown, Pause, Play, Settings, Save, TestTube
} from 'lucide-react';
import { api } from '../api/client';

// Category definitions with emojis
const CATEGORY_INFO = {
  health: { label: 'Health Summary', emoji: '🧭', description: 'Portfolio/project health indicators' },
  blockers: { label: 'Blockers', emoji: '🚧', description: 'Items with blocked status' },
  risks: { label: 'Risks', emoji: '⚠️', description: 'Top risks by severity' },
  issues: { label: 'Issues', emoji: '🧩', description: 'New or updated issues' },
  dependencies: { label: 'Dependencies', emoji: '🔗', description: 'Dependencies at risk' },
  decisions: { label: 'Decisions', emoji: '📝', description: 'Pending decisions' },
  milestones: { label: 'Milestones', emoji: '🗓️', description: 'Upcoming milestones' },
  overdue: { label: 'Overdue Items', emoji: '🚨', description: 'Past due items' },
  upcoming: { label: 'Upcoming (7 days)', emoji: '📅', description: 'Items due in next 7 days' },
  changes: { label: 'Recent Changes', emoji: '🔄', description: 'Changes in last 24 hours' }
};

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily', description: 'Every day at selected time(s)' },
  { value: 'weekdays', label: 'Weekdays', description: 'Monday to Friday' },
  { value: 'weekly', label: 'Weekly', description: 'Once per week on selected day' },
  { value: 'custom', label: 'Custom', description: 'Choose specific days' }
];

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const TIME_OPTIONS = ['06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', 
                      '14:00', '16:00', '18:00', '20:00'];

const DailySignalSettings = ({ onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Preferences state
  const [enabled, setEnabled] = useState(true);
  const [channel, setChannel] = useState('whatsapp');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [teamsEnabled, setTeamsEnabled] = useState(false);
  const [frequency, setFrequency] = useState('daily');
  const [daysOfWeek, setDaysOfWeek] = useState([1, 2, 3, 4, 5]);
  const [times, setTimes] = useState(['08:00']);
  const [categories, setCategories] = useState(['health', 'blockers', 'risks', 'overdue']);
  const [format, setFormat] = useState('short');
  const [scopeType, setScopeType] = useState('mine');
  const [scopeIds, setScopeIds] = useState([]);
  const [pausedUntil, setPausedUntil] = useState(null);
  
  // Policy restrictions
  const [policy, setPolicy] = useState({});
  const [availableCategories, setAvailableCategories] = useState([]);
  
  // Portfolios/Projects for scope selection
  const [portfolios, setPortfolios] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    loadPreferences();
    loadPortfoliosAndProjects();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const response = await api.getNotificationPreferences();
      const data = response.data;
      
      setPolicy(data.effective_policy || {});
      setAvailableCategories(data.categories_available || Object.keys(CATEGORY_INFO));
      
      if (data.preferences) {
        const prefs = data.preferences;
        setEnabled(prefs.enabled ?? true);
        setChannel(prefs.channel || 'whatsapp');
        setWhatsappNumber(prefs.whatsapp_number || '');
        setTeamsEnabled(prefs.teams_enabled ?? false);
        setFrequency(prefs.frequency || 'daily');
        setDaysOfWeek(prefs.days_of_week || [1, 2, 3, 4, 5]);
        setTimes(prefs.times || ['08:00']);
        setCategories(prefs.categories || ['health', 'blockers', 'risks', 'overdue']);
        setFormat(prefs.format || 'short');
        setScopeType(prefs.scope_type || 'mine');
        setScopeIds(prefs.scope_ids || []);
        setPausedUntil(prefs.paused_until ? new Date(prefs.paused_until) : null);
      }
    } catch (err) {
      setError('Failed to load notification preferences');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadPortfoliosAndProjects = async () => {
    try {
      const [portfolioRes, projectRes] = await Promise.all([
        api.getPortfolios(),
        api.getProjects()
      ]);
      // Portfolio API returns array directly, Projects API returns {projects: [], total: ...}
      setPortfolios(Array.isArray(portfolioRes.data) ? portfolioRes.data : portfolioRes.data?.portfolios || []);
      setProjects(Array.isArray(projectRes.data) ? projectRes.data : projectRes.data?.projects || []);
    } catch (err) {
      console.error('Failed to load portfolios/projects:', err);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      // Validate WhatsApp number if channel is whatsapp
      if (channel === 'whatsapp' && enabled && whatsappNumber) {
        const e164Regex = /^\+[1-9]\d{6,14}$/;
        if (!e164Regex.test(whatsappNumber)) {
          setError('WhatsApp number must be in E.164 format (e.g., +1234567890)');
          return;
        }
      }
      
      await api.saveNotificationPreferences({
        enabled,
        channel,
        whatsapp_number: whatsappNumber || null,
        teams_enabled: teamsEnabled,
        frequency,
        days_of_week: daysOfWeek,
        times,
        categories,
        format,
        scope_type: scopeType,
        scope_ids: scopeIds,
        paused_until: pausedUntil
      });
      
      setSuccess('Preferences saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    try {
      setSendingTest(true);
      setTestResult(null);
      setError(null);
      
      const response = await api.sendTestSignal();
      setTestResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send test signal');
    } finally {
      setSendingTest(false);
    }
  };

  const toggleDay = (day) => {
    if (daysOfWeek.includes(day)) {
      setDaysOfWeek(daysOfWeek.filter(d => d !== day));
    } else {
      setDaysOfWeek([...daysOfWeek, day].sort());
    }
  };

  const toggleTime = (time) => {
    if (times.includes(time)) {
      if (times.length > 1) {
        setTimes(times.filter(t => t !== time));
      }
    } else {
      setTimes([...times, time].sort());
    }
  };

  const toggleCategory = (cat) => {
    if (categories.includes(cat)) {
      setCategories(categories.filter(c => c !== cat));
    } else {
      setCategories([...categories, cat]);
    }
  };

  const isFieldDisabled = (field) => {
    // Check if admin has restricted this field
    if (field === 'custom_times' && !policy.allow_custom_times) return true;
    if (field === 'detailed' && !policy.allow_detailed) return true;
    return false;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--clr-brand-primary)]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[var(--clr-brand-primary)] bg-opacity-10 flex items-center justify-center">
            <Bell className="w-5 h-5 text-[var(--clr-brand-primary)]" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-[var(--clr-text-primary)]">Daily Signal™</h2>
            <p className="text-sm text-[var(--clr-text-muted)]">Configure your daily project insights delivery</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50 transition"
          data-testid="save-notifications-btn"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save Preferences
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-red-700">{error}</p>
        </div>
      )}
      
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          <p className="text-green-700">{success}</p>
        </div>
      )}

      {/* Enable/Disable Toggle */}
      <div className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {enabled ? (
              <Play className="w-5 h-5 text-green-500" />
            ) : (
              <Pause className="w-5 h-5 text-gray-400" />
            )}
            <div>
              <p className="font-medium text-[var(--clr-text-primary)]">
                Daily Signal {enabled ? 'Enabled' : 'Disabled'}
              </p>
              <p className="text-sm text-[var(--clr-text-muted)]">
                {enabled ? 'You will receive daily insights' : 'No signals will be sent'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setEnabled(!enabled)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              enabled ? 'bg-[var(--clr-brand-primary)]' : 'bg-gray-200'
            }`}
            data-testid="enable-signal-toggle"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {enabled && (
        <>
          {/* Channel Selection */}
          <div className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] p-4">
            <h3 className="font-medium text-[var(--clr-text-primary)] mb-4 flex items-center gap-2">
              <Smartphone className="w-4 h-4" />
              Delivery Channel
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <button
                onClick={() => setChannel('whatsapp')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  channel === 'whatsapp'
                    ? 'border-[var(--clr-brand-primary)] bg-[var(--clr-brand-primary)] text-white'
                    : 'border-[var(--clr-border-light)] hover:border-[var(--clr-border-medium)]'
                }`}
                data-testid="channel-whatsapp"
              >
                <MessageSquare className={`w-6 h-6 mx-auto mb-2 ${
                  channel === 'whatsapp' ? 'text-white' : 'text-gray-400'
                }`} />
                <p className={`font-medium ${channel === 'whatsapp' ? 'text-white' : 'text-[var(--clr-text-secondary)]'}`}>
                  WhatsApp
                </p>
                <p className={`text-xs mt-1 ${channel === 'whatsapp' ? 'text-white text-opacity-80' : 'text-[var(--clr-text-muted)]'}`}>Primary</p>
              </button>
              <button
                onClick={() => setChannel('email')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  channel === 'email'
                    ? 'border-[var(--clr-brand-primary)] bg-[var(--clr-brand-primary)] text-white'
                    : 'border-[var(--clr-border-light)] hover:border-[var(--clr-border-medium)]'
                }`}
                data-testid="channel-email"
              >
                <Mail className={`w-6 h-6 mx-auto mb-2 ${
                  channel === 'email' ? 'text-white' : 'text-gray-400'
                }`} />
                <p className={`font-medium ${channel === 'email' ? 'text-white' : 'text-[var(--clr-text-secondary)]'}`}>
                  Email
                </p>
                <p className={`text-xs mt-1 ${channel === 'email' ? 'text-white text-opacity-80' : 'text-[var(--clr-text-muted)]'}`}>Fallback</p>
              </button>
              <button
                onClick={() => setChannel('teams')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  channel === 'teams'
                    ? 'border-purple-500 bg-purple-600 text-white'
                    : 'border-[var(--clr-border-light)] hover:border-[var(--clr-border-medium)]'
                }`}
                data-testid="channel-teams"
              >
                <svg className={`w-6 h-6 mx-auto mb-2 ${channel === 'teams' ? 'text-white' : 'text-gray-400'}`} viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 8.5h-2.5V7c0-1.4-1.1-2.5-2.5-2.5h-6C7.6 4.5 6.5 5.6 6.5 7v1.5H4c-1.1 0-2 .9-2 2v7c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-7c0-1.1-.9-2-2-2zM8.5 7c0-.3.2-.5.5-.5h6c.3 0 .5.2.5.5v1.5h-7V7zm11.5 10.5H4v-7h16v7z"/>
                </svg>
                <p className={`font-medium ${channel === 'teams' ? 'text-white' : 'text-[var(--clr-text-secondary)]'}`}>
                  Teams
                </p>
                <p className={`text-xs mt-1 ${channel === 'teams' ? 'text-white text-opacity-80' : 'text-[var(--clr-text-muted)]'}`}>Channel</p>
              </button>
            </div>
            
            {/* Also send to Teams (additional channel) */}
            {channel !== 'teams' && (
              <div className="mt-4 p-3 bg-purple-50 border border-purple-100 rounded-lg">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={teamsEnabled}
                    onChange={(e) => setTeamsEnabled(e.target.checked)}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <div>
                    <p className="text-sm font-medium text-purple-900">Also send to Microsoft Teams</p>
                    <p className="text-xs text-purple-600">Post to Teams channel in addition to {channel}</p>
                  </div>
                </label>
              </div>
            )}
            
            {channel === 'whatsapp' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-2">
                  WhatsApp Number (E.164 format)
                </label>
                <input
                  type="tel"
                  value={whatsappNumber}
                  onChange={(e) => setWhatsappNumber(e.target.value)}
                  placeholder="+1234567890"
                  className="w-full px-3 py-2 border border-[var(--clr-border-medium)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:border-transparent"
                  data-testid="whatsapp-number-input"
                />
                <p className="text-xs text-[var(--clr-text-muted)] mt-1">
                  Include country code (e.g., +1 for USA, +27 for South Africa)
                </p>
              </div>
            )}

            {channel === 'teams' && (
              <div className="mt-4 p-3 bg-[var(--clr-surface-subtle)] rounded-lg">
                <p className="text-sm text-[var(--clr-text-secondary)]">
                  <Info className="w-4 h-4 inline mr-1" />
                  Teams delivery targets are managed by your administrator in the notification settings.
                </p>
              </div>
            )}
          </div>

          {/* Frequency & Schedule */}
          <div className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] p-4">
            <h3 className="font-medium text-[var(--clr-text-primary)] mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Schedule
            </h3>
            
            {/* Frequency */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-2">
                Frequency
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {FREQUENCY_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      if (opt.value === 'custom' && isFieldDisabled('custom_times')) return;
                      setFrequency(opt.value);
                    }}
                    disabled={opt.value === 'custom' && isFieldDisabled('custom_times')}
                    className={`px-3 py-2 rounded-lg border text-sm transition-all ${
                      frequency === opt.value
                        ? 'border-[var(--clr-brand-primary)] bg-[var(--clr-brand-primary)] text-white'
                        : 'border-[var(--clr-border-light)] hover:border-[var(--clr-border-medium)] text-[var(--clr-text-secondary)]'
                    } ${opt.value === 'custom' && isFieldDisabled('custom_times') ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={opt.value === 'custom' && isFieldDisabled('custom_times') ? 'Disabled by administrator' : opt.description}
                    data-testid={`frequency-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Days of Week (for custom or weekly) */}
            {(frequency === 'custom' || frequency === 'weekly') && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-2">
                  Days
                </label>
                <div className="flex flex-wrap gap-2">
                  {DAY_NAMES.map((day, idx) => (
                    <button
                      key={day}
                      onClick={() => toggleDay(idx + 1)}
                      className={`w-10 h-10 rounded-lg border text-sm font-medium transition-all ${
                        daysOfWeek.includes(idx + 1)
                          ? 'border-[var(--clr-brand-primary)] bg-[var(--clr-brand-primary)] text-white'
                          : 'border-[var(--clr-border-light)] hover:border-[var(--clr-border-medium)] text-[var(--clr-text-secondary)]'
                      }`}
                      data-testid={`day-${day.toLowerCase()}`}
                    >
                      {day[0]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Time Selection */}
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-2">
                Time(s)
              </label>
              <div className="flex flex-wrap gap-2">
                {TIME_OPTIONS.map(time => (
                  <button
                    key={time}
                    onClick={() => toggleTime(time)}
                    className={`px-3 py-1.5 rounded-lg border text-sm transition-all ${
                      times.includes(time)
                        ? 'border-[var(--clr-brand-primary)] bg-[var(--clr-brand-primary)] text-white'
                        : 'border-[var(--clr-border-light)] hover:border-[var(--clr-border-medium)] text-[var(--clr-text-secondary)]'
                    }`}
                    data-testid={`time-${time.replace(':', '')}`}
                  >
                    {time}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Scope Selection */}
          <div className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] p-4">
            <h3 className="font-medium text-[var(--clr-text-primary)] mb-4 flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Data Scope
            </h3>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
              {['mine', 'project', 'portfolio', 'pmo'].map(scope => (
                <button
                  key={scope}
                  onClick={() => setScopeType(scope)}
                  className={`px-3 py-2 rounded-lg border text-sm transition-all capitalize ${
                    scopeType === scope
                      ? 'border-[var(--clr-brand-primary)] bg-[var(--clr-brand-primary)] text-white'
                      : 'border-[var(--clr-border-light)] hover:border-[var(--clr-border-medium)] text-[var(--clr-text-secondary)]'
                  }`}
                  data-testid={`scope-${scope}`}
                >
                  {scope === 'mine' ? 'My Items' : scope === 'pmo' ? 'All (PMO)' : `My ${scope}s`}
                </button>
              ))}
            </div>

            {scopeType === 'portfolio' && (
              <div>
                <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-2">
                  Select Portfolios
                </label>
                <div className="max-h-40 overflow-y-auto border border-[var(--clr-border-light)] rounded-lg p-2">
                  {portfolios.map(p => (
                    <label key={p.id} className="flex items-center gap-2 p-2 hover:bg-[var(--clr-surface-hover)] rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={scopeIds.includes(p.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setScopeIds([...scopeIds, p.id]);
                          } else {
                            setScopeIds(scopeIds.filter(id => id !== p.id));
                          }
                        }}
                        className="w-4 h-4 text-[var(--clr-brand-primary)] rounded"
                      />
                      <span className="text-sm text-[var(--clr-text-primary)]">{p.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {scopeType === 'project' && (
              <div>
                <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-2">
                  Select Projects
                </label>
                <div className="max-h-40 overflow-y-auto border border-[var(--clr-border-light)] rounded-lg p-2">
                  {projects.map(p => (
                    <label key={p.id} className="flex items-center gap-2 p-2 hover:bg-[var(--clr-surface-hover)] rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={scopeIds.includes(p.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setScopeIds([...scopeIds, p.id]);
                          } else {
                            setScopeIds(scopeIds.filter(id => id !== p.id));
                          }
                        }}
                        className="w-4 h-4 text-[var(--clr-brand-primary)] rounded"
                      />
                      <span className="text-sm text-[var(--clr-text-primary)]">{p.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Categories */}
          <div className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] p-4">
            <h3 className="font-medium text-[var(--clr-text-primary)] mb-4 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Categories to Include
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.entries(CATEGORY_INFO).map(([key, info]) => {
                const isSelected = categories.includes(key);
                return (
                  <label
                    key={key}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      isSelected
                        ? 'border-[var(--clr-brand-primary)] bg-[var(--clr-brand-primary)] text-white'
                        : 'border-[var(--clr-border-light)] hover:border-[var(--clr-border-medium)]'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleCategory(key)}
                      className={`w-4 h-4 rounded ${isSelected ? 'accent-white' : 'text-[var(--clr-brand-primary)]'}`}
                      data-testid={`category-${key}`}
                    />
                    <span className="text-lg">{info.emoji}</span>
                    <div>
                      <p className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-[var(--clr-text-primary)]'}`}>{info.label}</p>
                      <p className={`text-xs ${isSelected ? 'text-white text-opacity-80' : 'text-[var(--clr-text-muted)]'}`}>{info.description}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Format */}
          <div className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] p-4">
            <h3 className="font-medium text-[var(--clr-text-primary)] mb-4">Message Format</h3>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setFormat('short')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  format === 'short'
                    ? 'border-[var(--clr-brand-primary)] bg-[var(--clr-brand-primary)] text-white'
                    : 'border-[var(--clr-border-light)] hover:border-[var(--clr-border-medium)]'
                }`}
                data-testid="format-short"
              >
                <p className={`font-medium ${format === 'short' ? 'text-white' : 'text-[var(--clr-text-secondary)]'}`}>
                  Short
                </p>
                <p className={`text-xs mt-1 ${format === 'short' ? 'text-white text-opacity-80' : 'text-[var(--clr-text-muted)]'}`}>
                  Counts only, quick glance
                </p>
              </button>
              <button
                onClick={() => {
                  if (isFieldDisabled('detailed')) return;
                  setFormat('detailed');
                }}
                disabled={isFieldDisabled('detailed')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  format === 'detailed'
                    ? 'border-[var(--clr-brand-primary)] bg-[var(--clr-brand-primary)] text-white'
                    : 'border-[var(--clr-border-light)] hover:border-[var(--clr-border-medium)]'
                } ${isFieldDisabled('detailed') ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={isFieldDisabled('detailed') ? 'Disabled by administrator' : 'Includes item names'}
                data-testid="format-detailed"
              >
                <p className={`font-medium ${format === 'detailed' ? 'text-white' : 'text-[var(--clr-text-secondary)]'}`}>
                  Detailed
                </p>
                <p className={`text-xs mt-1 ${format === 'detailed' ? 'text-white text-opacity-80' : 'text-[var(--clr-text-muted)]'}`}>
                  Includes item names
                </p>
              </button>
            </div>
          </div>

          {/* Test Signal */}
          <div className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-[var(--clr-text-primary)]">Send Test Signal</h3>
                <p className="text-sm text-[var(--clr-text-muted)]">
                  Preview how your Daily Signal will look
                </p>
              </div>
              <button
                onClick={handleSendTest}
                disabled={sendingTest}
                className="flex items-center gap-2 px-4 py-2 border border-[var(--clr-brand-primary)] text-[var(--clr-brand-primary)] rounded-lg hover:bg-[var(--clr-brand-primary)] hover:text-white transition disabled:opacity-50"
                data-testid="send-test-btn"
              >
                {sendingTest ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                ) : (
                  <TestTube className="w-4 h-4" />
                )}
                Send Test
              </button>
            </div>

            {testResult && (
              <div className="mt-4 p-4 bg-[var(--clr-surface-subtle)] rounded-lg">
                <p className="text-sm font-medium text-[var(--clr-text-primary)] mb-2">Preview:</p>
                <pre className="text-sm text-[var(--clr-text-secondary)] whitespace-pre-wrap font-mono bg-white p-3 rounded border border-[var(--clr-border-light)]">
                  {testResult.preview?.message || 'No preview available'}
                </pre>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DailySignalSettings;
